class MiniCart extends HTMLElement {
  constructor() {
    super();

    // Bumped by every write into #mini-cart. The lazy hydration fetch captures
    // it before requesting and drops its response if anything rendered fresher
    // markup meanwhile, so a slow hydration can never resurrect a stale cart.
    this.renderVersion = 0;
  }

  connectedCallback() {
    this.header = document.querySelector('sticky-header');
    this.drawer = document.querySelector('cart-drawer');
    this.observeCartUpdates();
    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);
  }

  observeCartUpdates() {
    this.freeShippingObserver = new MutationObserver(() => {
      this.updateFreeShippingProgress();
    });
    this.freeShippingObserver.observe(this, { childList: true, subtree: true });
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);
    this.hydrate();
  }

  hydrate() {
    // renderContents already filled the drawer from a cart mutation, so its
    // markup is newer than anything this fetch could return.
    if (this.querySelector('form.mini-cart')) {
      document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
      return;
    }

    const version = this.renderVersion;
    this.hydrationRequest?.abort();
    this.hydrationRequest = new AbortController();

    fetch(this.getSectionUrl(), { signal: this.hydrationRequest.signal })
      .then(response => response.text())
      .then(html => {
        if (version !== this.renderVersion) return;

        this.renderVersion += 1;
        document.getElementById('mini-cart').innerHTML =
          this.getSectionInnerHTML(html, '.shopify-section');

        this.updateFreeShippingProgress();

        document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
      })
      .catch(e => {
        if (e.name === 'AbortError') return;
        console.error(e);
      });
  }

  getSectionUrl() {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', 'mini-cart');
    return url.toString();
  }

  open() {
    const detailsElement = this.drawer.querySelector('details');
    if (detailsElement.hasAttribute('open')) {
      return;
    }
    
    this.drawer.openMenuDrawer();
  }

  renderContents(parsedState) {
    this.renderVersion += 1;
    this.hydrationRequest?.abort();

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      if (document.getElementById(section.id)) {
        document.getElementById(section.id).innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    }));

    this.open();
    this.updateFreeShippingProgress();
  }

  updateFreeShippingProgress() {
    if (this.isUpdatingFreeShippingProgress) return;

    const freeShipping = this.querySelector('.free-shipping[data-market-thresholds]');
    if (!freeShipping || !window.shopSettings?.countryIsoCode) return;

    const threshold = this.getThresholdForCountry(
      freeShipping.dataset.marketThresholds,
      window.shopSettings.countryIsoCode
    );
    const totalPrice = Number(freeShipping.dataset.cartTotalPrice || 0);
    if (!threshold || Number.isNaN(totalPrice)) return;

    const thresholdInCents = Math.round(threshold * 100);
    if (thresholdInCents <= 0) return;

    this.isUpdatingFreeShippingProgress = true;

    // With a gift tier the bar runs to the gift threshold and the truck sits at
    // the shipping threshold on that same scale (see free-shipping.liquid), so
    // the fill only passes the truck once free shipping is actually reached.
    const giftThresholdInCents = Number(freeShipping.dataset.giftThreshold || 0);
    const hasGiftTier = giftThresholdInCents > thresholdInCents;
    const barMaxInCents = hasGiftTier ? giftThresholdInCents : thresholdInCents;

    const progress = Math.min(Math.round((totalPrice / barMaxInCents) * 100), 100);
    const isQualified = totalPrice >= thresholdInCents;
    const isGiftQualified = hasGiftTier && totalPrice >= giftThresholdInCents;
    const giftMessageTemplate = freeShipping.dataset.secondLevelMessageTemplate;
    const showsGiftMessage = isQualified && hasGiftTier && !isGiftQualified && !!giftMessageTemplate;
    const text = freeShipping.querySelector('.free-shipping__text');
    const progressBar = freeShipping.querySelector('.free-shipping__progress');

    freeShipping.querySelectorAll('style').forEach((style) => style.remove());

    if (text) {
      let nextText = freeShipping.dataset.successMessage;
      if (!isQualified) {
        nextText = this.getRemainingMessage(
          freeShipping.dataset.firstLevelMessageTemplate,
          thresholdInCents - totalPrice
        );
      } else if (showsGiftMessage) {
        nextText = this.getRemainingMessage(giftMessageTemplate, giftThresholdInCents - totalPrice);
      }

      text.classList.toggle('free-shipping__text--success', isQualified && !showsGiftMessage);
      if (text.innerHTML !== nextText) {
        text.innerHTML = nextText;
      }
    }

    if (progressBar) {
      const nextProgress = `${progress}%`;
      if (progressBar.style.getPropertyValue('--progress') !== nextProgress) {
        progressBar.style.setProperty('--progress', nextProgress);
      }

      const nextFirstIconPosition = hasGiftTier ? `${(thresholdInCents / giftThresholdInCents) * 100}%` : '';
      if (progressBar.style.getPropertyValue('--first-icon-position') !== nextFirstIconPosition) {
        progressBar.style.setProperty('--first-icon-position', nextFirstIconPosition);
      }
      progressBar.classList.toggle('free-shipping__progress--success', isQualified);
    }

    this.updateCartShippingNote();

    this.isUpdatingFreeShippingProgress = false;
  }

  updateCartShippingNote() {
    const note = this.querySelector('[data-cart-shipping-note]');
    if (!note || !window.shopSettings?.countryIsoCode) return;

    const threshold = this.getThresholdForCountry(
      note.dataset.marketThresholds,
      window.shopSettings.countryIsoCode
    );
    const totalPrice = Number(note.dataset.cartTotalPrice || 0);
    if (!threshold || Number.isNaN(totalPrice)) return;

    const isQualified = totalPrice >= Math.round(threshold * 100);
    const nextNote = isQualified ? note.dataset.freeShippingNote : note.dataset.paidShippingNote;
    if (nextNote && note.innerHTML.trim() !== nextNote) {
      note.innerHTML = nextNote;
    }
  }

  getThresholdForCountry(thresholds, countryCode) {
    if (!thresholds) return null;

    const normalizedCountryCode = countryCode.toUpperCase();
    const currencyCode = window.shopSettings?.isoCode?.toUpperCase();
    const pairs = thresholds.replace(/\s/g, '').split(',');

    for (const pair of pairs) {
      const [code, amount] = pair.split(':');
      const normalizedCode = code?.toUpperCase();
      if ((normalizedCode === normalizedCountryCode || normalizedCode === currencyCode) && amount) {
        return Number.parseFloat(amount.replace(',', '.'));
      }
    }

    return null;
  }

  getRemainingMessage(template, remainingAmount) {
    const remainingHtml = `<price-money class="price"><bdi>${theme.Currency.formatMoney(remainingAmount, window.shopSettings.moneyFormat)}</bdi></price-money>`;
    return (template || '').replace('|amount|', remainingHtml);
  }

  getSectionsToRender() {
    return [
      {
        id: 'mini-cart',
        section: 'mini-cart',
        selector: '.shopify-section'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }
}

customElements.define('mini-cart', MiniCart);
